import "./artwork.css";
import { copyToClipboard } from "./clipboard.js";

const SHOPIFY_CDN = "https://cdn.shopify.com/s/files/1/0706/9902/4648/files";
const YOKAI_PROJECT_URL = "https://mycatportal.com/pages/yokai";
const FEEDBACK_STYLE_ID = "yokai-export-feedback-styles";
const FEEDBACK_NODE_ID = "yokai-export-feedback";

export const HERO_ART_URL = `${SHOPIFY_CDN}/yokai-builder-hero.jpg?v=1785407147`;
export const SIDEBAR_PROMO_URL = `${SHOPIFY_CDN}/yokai-builder-sidebar-promo.jpg?v=1785407147`;

export const SPECIES_ART_URLS: Record<string, string> = {
  "species.kappa": `${SHOPIFY_CDN}/yokai-builder-species-kappa.jpg?v=1785407148`,
  "species.hanyo": `${SHOPIFY_CDN}/yokai-builder-species-hanyo.jpg?v=1785407147`,
  "species.karakasa_kozo": `${SHOPIFY_CDN}/yokai-builder-species-karakasa-kozo.jpg?v=1785407147`,
  "species.noppera_bo": `${SHOPIFY_CDN}/yokai-builder-species-noppera-bo.jpg?v=1785407147`,
  "species.mizuchi": `${SHOPIFY_CDN}/yokai-builder-species-mizuchi.jpg?v=1785407147`,
  "species.medama": `${SHOPIFY_CDN}/yokai-builder-species-medama.jpg?v=1785407147`,
  "species.inugami_jiyuu": `${SHOPIFY_CDN}/yokai-builder-species-inugami-jiyuu.jpg?v=1785407147`,
  "species.oni": `${SHOPIFY_CDN}/yokai-builder-species-oni.jpg?v=1785407147`,
  "species.yuki_onna": `${SHOPIFY_CDN}/yokai-builder-species-yuki-onna.jpg?v=1785407147`,
  "species.tanuki": `${SHOPIFY_CDN}/yokai-builder-species-tanuki.jpg?v=1785407147`,
  "species.kitsune": `${SHOPIFY_CDN}/yokai-builder-species-kitsune.jpg?v=1785407147`,
  "species.jorogumo": `${SHOPIFY_CDN}/yokai-builder-species-jorogumo.jpg?v=1785407147`,
  "species.rokurokubi": `${SHOPIFY_CDN}/yokai-builder-species-rokurokubi.jpg?v=1785407147`,
  "species.bakeneko": `${SHOPIFY_CDN}/yokai-builder-species-bakeneko.jpg?v=1785407148`,
  "species.raiju": `${SHOPIFY_CDN}/yokai-builder-species-raiju.jpg?v=1785407148`,
  "species.yamabushi_tengu": `${SHOPIFY_CDN}/yokai-builder-species-yamabushi-tengu.jpg?v=1785407148`,
};

let feedbackTimer: number | null = null;
let pdfProgressActive = false;
let pdfProgressFirstClickPending = false;
let pdfProgressButton: HTMLButtonElement | null = null;
let pdfProgressObserver: MutationObserver | null = null;
let pdfProgressTimeout: number | null = null;
let pdfProgressStartedAt = 0;
let pdfProgressPreviousNotice = "";

function mountSidebarPromo(): void {
  document.querySelectorAll<HTMLElement>(".builder-shell .draft-summary").forEach((summary) => {
    if (summary.querySelector(".draft-promo")) return;

    const link = document.createElement("a");
    link.className = "draft-promo";
    link.href = YOKAI_PROJECT_URL;
    link.target = "_top";
    link.setAttribute("aria-label", "Discover the Yōkai tabletop roleplaying game");

    const image = document.createElement("img");
    image.src = SIDEBAR_PROMO_URL;
    image.alt = "";

    const copy = document.createElement("span");
    copy.className = "draft-promo-copy";
    copy.innerHTML = [
      '<span class="draft-promo-kicker">PLAY THE DEMON</span>',
      "<strong>Enter the world of Yōkai!</strong>",
      '<span class="draft-promo-tagline">Modern Japan. Ancient nightmares. One d12.</span>',
      '<span class="draft-promo-button">Discover the game</span>',
    ].join("");

    link.append(image, copy);
    summary.prepend(link);
  });
}

function ensureExportFeedbackStyles(): void {
  if (document.getElementById(FEEDBACK_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = FEEDBACK_STYLE_ID;
  style.textContent = `
    #${FEEDBACK_NODE_ID} {
      position: fixed;
      left: 50%;
      bottom: 24px;
      z-index: 2147483647;
      display: flex;
      align-items: center;
      gap: 10px;
      max-width: calc(100vw - 32px);
      padding: 12px 16px;
      border: 1px solid rgba(255,255,255,.16);
      border-radius: 999px;
      background: rgba(17,17,17,.96);
      color: #fff;
      box-shadow: 0 12px 36px rgba(0,0,0,.28);
      font: 700 14px/1.2 system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      letter-spacing: .01em;
      pointer-events: none;
      opacity: 0;
      transform: translate(-50%, 10px);
      transition: opacity .18s ease, transform .18s ease;
    }
    #${FEEDBACK_NODE_ID}.is-visible {
      opacity: 1;
      transform: translate(-50%, 0);
    }
    #${FEEDBACK_NODE_ID}.is-success {
      border-color: rgba(255,255,255,.28);
    }
    #${FEEDBACK_NODE_ID}.is-error {
      border-color: rgba(220,68,55,.8);
    }
    #${FEEDBACK_NODE_ID} .yokai-feedback-spinner {
      width: 16px;
      height: 16px;
      flex: 0 0 16px;
      border: 2px solid rgba(255,255,255,.28);
      border-top-color: #fff;
      border-radius: 50%;
      animation: yokai-feedback-spin .7s linear infinite;
    }
    @keyframes yokai-feedback-spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.append(style);
}

function feedbackNode(): HTMLDivElement {
  ensureExportFeedbackStyles();
  let node = document.getElementById(FEEDBACK_NODE_ID) as HTMLDivElement | null;
  if (node) return node;
  node = document.createElement("div");
  node.id = FEEDBACK_NODE_ID;
  node.setAttribute("role", "status");
  node.setAttribute("aria-live", "polite");
  document.body.append(node);
  return node;
}

function showFeedback(message: string, options?: { spinner?: boolean; error?: boolean; duration?: number }): void {
  const node = feedbackNode();
  if (feedbackTimer !== null) {
    window.clearTimeout(feedbackTimer);
    feedbackTimer = null;
  }

  node.className = options?.error ? "is-error" : "is-success";
  node.replaceChildren();
  if (options?.spinner) {
    const spinner = document.createElement("span");
    spinner.className = "yokai-feedback-spinner";
    spinner.setAttribute("aria-hidden", "true");
    node.append(spinner);
  }
  const label = document.createElement("span");
  label.textContent = message;
  node.append(label);
  requestAnimationFrame(() => node.classList.add("is-visible"));

  if (options?.duration) {
    feedbackTimer = window.setTimeout(() => hideFeedback(), options.duration);
  }
}

function hideFeedback(): void {
  if (feedbackTimer !== null) {
    window.clearTimeout(feedbackTimer);
    feedbackTimer = null;
  }
  document.getElementById(FEEDBACK_NODE_ID)?.classList.remove("is-visible");
}

function enableDiscordClipboardFeedback(): void {
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest<HTMLButtonElement>(".builder-shell .discord-code button");
    if (!button) return;
    const code = button.closest(".discord-code")?.querySelector("strong")?.textContent?.trim();
    if (!code) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const originalText = button.textContent || "Copy code";
    void copyToClipboard(code)
      .then(() => {
        button.textContent = "Copied!";
        showFeedback("Code copied to clipboard.", { duration: 1800 });
        window.setTimeout(() => { button.textContent = originalText; }, 1600);
      })
      .catch(() => {
        button.textContent = "Copy manually";
        showFeedback("Copy failed. Please copy the code manually.", { error: true, duration: 2800 });
        window.setTimeout(() => { button.textContent = originalText; }, 2400);
      });
  }, true);
}

function finishPdfProgress(): void {
  pdfProgressActive = false;
  pdfProgressFirstClickPending = false;
  pdfProgressButton = null;
  pdfProgressObserver?.disconnect();
  pdfProgressObserver = null;
  if (pdfProgressTimeout !== null) {
    window.clearTimeout(pdfProgressTimeout);
    pdfProgressTimeout = null;
  }
  hideFeedback();
}

function finishPdfProgressAfterMinimum(): void {
  const minimumVisibleMs = 850;
  const remaining = Math.max(0, minimumVisibleMs - (performance.now() - pdfProgressStartedAt));
  window.setTimeout(() => finishPdfProgress(), remaining);
}

function beginPdfProgress(button: HTMLButtonElement, firstClickPending: boolean): void {
  if (pdfProgressActive) return;
  const panel = button.closest<HTMLElement>(".wizard-panel");
  if (!panel) return;

  pdfProgressActive = true;
  pdfProgressFirstClickPending = firstClickPending;
  pdfProgressButton = button;
  pdfProgressStartedAt = performance.now();
  pdfProgressPreviousNotice = panel.querySelector<HTMLElement>(".notice[role='status']")?.textContent?.trim() || "";
  showFeedback("Generating character sheet…", { spinner: true });

  pdfProgressObserver?.disconnect();
  pdfProgressObserver = new MutationObserver(() => {
    const notice = panel.querySelector<HTMLElement>(".notice[role='status']");
    const nextNotice = notice?.textContent?.trim() || "";
    if (nextNotice && nextNotice !== pdfProgressPreviousNotice) finishPdfProgressAfterMinimum();
  });
  pdfProgressObserver.observe(panel, { childList: true, subtree: true, characterData: true });

  if (pdfProgressTimeout !== null) window.clearTimeout(pdfProgressTimeout);
  pdfProgressTimeout = window.setTimeout(() => finishPdfProgress(), 30000);
}

function pdfButtonFromTarget(target: EventTarget | null): HTMLButtonElement | null {
  if (!(target instanceof Element)) return null;
  const button = target.closest<HTMLButtonElement>(".guest-export-card .export-actions button.primary");
  if (!button || button.disabled) return null;
  const actions = button.closest<HTMLElement>(".export-actions");
  if (!actions || actions.querySelector("button.primary") !== button) return null;
  return button;
}

function enablePdfProgressFeedback(): void {
  document.addEventListener("pointerdown", (event) => {
    const button = pdfButtonFromTarget(event.target);
    if (!button || pdfProgressActive) return;
    beginPdfProgress(button, true);
  }, true);

  document.addEventListener("click", (event) => {
    const button = pdfButtonFromTarget(event.target);
    if (!button) return;

    if (!pdfProgressActive) {
      beginPdfProgress(button, false);
      return;
    }

    if (pdfProgressFirstClickPending && pdfProgressButton === button) {
      pdfProgressFirstClickPending = false;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
}

if (typeof document !== "undefined" && typeof MutationObserver !== "undefined") {
  const observer = new MutationObserver(mountSidebarPromo);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  mountSidebarPromo();
  enableDiscordClipboardFeedback();
  enablePdfProgressFeedback();
}

export function speciesArtUrl(speciesId: string): string | undefined {
  return SPECIES_ART_URLS[speciesId];
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Artwork could not be decoded."));
    };
    image.src = objectUrl;
  });
}

export async function imageUrlToDataUrl(url: string): Promise<string> {
  const response = await fetch(url, { mode: "cors", credentials: "omit" });
  if (!response.ok) throw new Error(`Artwork could not be loaded (HTTP ${response.status}).`);
  const image = await loadImage(await response.blob());
  const maximumSide = 1200;
  const scale = Math.min(1, maximumSide / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Artwork could not be prepared for PDF export.");
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.82);
}